import { useMemo, useState } from 'react';
import {
  Badge, Button, Dropdown, Input, Option, Spinner, Text,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useT } from '../i18n';
import { usePermission } from '../hooks/usePermission';
import { apiErrorMessage } from '../api/apiError';
import { fetchProducts } from '../api/inventory.api';
import { fetchCrops, createCrop } from '../api/terrain.api';
import {
  MARKETS, fetchComplianceRules, createComplianceRule, updateComplianceRule, deleteComplianceRule,
  type ComplianceRule, type ComplianceRuleInput,
} from '../api/compliance-rules.api';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { ComplianceRuleDialog } from '../components/compliance/ComplianceRuleDialog';
import { ComplianceImportDialog } from '../components/compliance/ComplianceImportDialog';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'flex-end' },
  scroll:  { overflowX: 'auto' },
  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td:      { padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
  empty:   { padding: '14px 16px', border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  muted:   { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

const ALL = '';

export function ComplianceRulesPage() {
  const s = useStyles();
  const t = useT();
  const c = t.complianceRules;
  const { tenantId } = useParams();
  const queryClient = useQueryClient();
  const canWrite = usePermission('compliance:write');

  const [productFilter, setProductFilter] = useState(ALL);
  const [cropFilter, setCropFilter] = useState('');
  const [marketFilter, setMarketFilter] = useState(ALL);
  const [editing, setEditing] = useState<ComplianceRule | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toDelete, setToDelete] = useState<ComplianceRule | null>(null);
  const [serverError, setServerError] = useState<string | undefined>();

  const rulesQ = useQuery({ queryKey: ['complianceRules'], queryFn: () => fetchComplianceRules() });
  const productsQ = useQuery({ queryKey: ['products', 'AGROQUIMICO'], queryFn: () => fetchProducts('AGROQUIMICO') });
  const cropsQ = useQuery({ queryKey: ['crops'], queryFn: fetchCrops });

  const productOptions = useMemo(
    () => (productsQ.data ?? []).map((p) => ({ id: p.productId, label: p.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [productsQ.data],
  );
  const cropNames = useMemo(() => (cropsQ.data ?? []).map((x) => x.name).sort((a, b) => a.localeCompare(b)), [cropsQ.data]);

  const rules = rulesQ.data ?? [];
  const filtered = useMemo(() => rules.filter((r) =>
    (!productFilter || r.productId === productFilter)
    && (!cropFilter.trim() || r.crop.toLowerCase().includes(cropFilter.trim().toLowerCase()))
    && (!marketFilter || r.market === marketFilter)), [rules, productFilter, cropFilter, marketFilter]);

  const afterWrite = () => {
    queryClient.invalidateQueries({ queryKey: ['complianceRules'] });
    // El paso "Productos y reglas" del onboarding se marca con la primera regla.
    queryClient.invalidateQueries({ queryKey: ['onboardingStatus', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['onboardingStatus'] });
  };

  /** Alta libre de cultivo: si no existe en el catálogo se intenta crear (sin bloquear la regla). */
  const ensureCrop = async (name: string) => {
    if (cropNames.some((x) => x.trim().toLowerCase() === name.trim().toLowerCase())) return;
    try {
      await createCrop({ name });
      queryClient.invalidateQueries({ queryKey: ['crops'] });
    } catch { /* sin crop:write o duplicado: la regla se guarda igual */ }
  };

  const saveM = useMutation({
    mutationFn: async (input: ComplianceRuleInput) => {
      const saved = editing ? await updateComplianceRule(editing.ruleId, input) : await createComplianceRule(input);
      await ensureCrop(input.crop);
      return saved;
    },
    onSuccess: () => { setFormOpen(false); setEditing(null); setServerError(undefined); afterWrite(); },
    onError: (e) => setServerError(apiErrorMessage(e, c.errorGeneric)),
  });

  const deleteM = useMutation({
    mutationFn: (rule: ComplianceRule) => deleteComplianceRule(rule.ruleId),
    onSuccess: () => { setToDelete(null); afterWrite(); },
  });

  const openNew = () => { setEditing(null); setServerError(undefined); setFormOpen(true); };
  const openEdit = (r: ComplianceRule) => { setEditing(r); setServerError(undefined); setFormOpen(true); };

  const mrlText = (r: ComplianceRule) => `${r.mrlValue ?? r.mrlMgPerKg} ${r.mrlUnit ?? 'mg/kg'}`;

  return (
    <div className={s.root}>
      <div className={s.header}>
        <div>
          <PageTitle>{c.title}</PageTitle>
          <div><Text className={s.muted}>{c.subtitle}</Text></div>
        </div>
        {canWrite && (
          <div className={s.actions}>
            <Button onClick={() => setImportOpen(true)}>{c.importExcel}</Button>
            <Button appearance="primary" onClick={openNew} disabled={productOptions.length === 0}>{c.newRule}</Button>
          </div>
        )}
      </div>

      <div className={s.filters}>
        <div style={{ minWidth: 220 }}>
          <SearchableSelect
            label={c.filterProduct}
            placeholder={c.allProducts}
            options={[{ id: ALL, label: c.allProducts }, ...productOptions]}
            value={productFilter}
            onChange={setProductFilter}
          />
        </div>
        <Input aria-label={c.filterCrop} placeholder={c.filterCrop} value={cropFilter} onChange={(_, d) => setCropFilter(d.value)} />
        <Dropdown
          aria-label={c.filterMarket}
          value={marketFilter || c.allMarkets}
          selectedOptions={[marketFilter]}
          onOptionSelect={(_, d) => setMarketFilter((d.optionValue as string) ?? ALL)}
        >
          <Option value={ALL}>{c.allMarkets}</Option>
          {MARKETS.map((m) => <Option key={m} value={m}>{m}</Option>)}
        </Dropdown>
      </div>

      {rulesQ.isLoading ? (
        <Spinner label={c.loading} />
      ) : rules.length === 0 ? (
        <div className={s.empty}>
          <Text weight="semibold">{c.emptyTitle}</Text>
          <Text className={s.muted}>{c.emptyHint}</Text>
          {!productsQ.isLoading && productOptions.length === 0 && <Text className={s.muted}>{c.emptyNoProducts}</Text>}
        </div>
      ) : filtered.length === 0 ? (
        <div className={s.empty}><Text className={s.muted}>{c.noMatches}</Text></div>
      ) : (
        <div className={s.scroll}>
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{c.colProduct}</th>
                <th className={s.th}>{c.colCrop}</th>
                <th className={s.th}>{c.colMarket}</th>
                <th className={s.th}>{c.colPhi}</th>
                <th className={s.th}>{c.colRei}</th>
                <th className={s.th}>{c.colMrl}</th>
                <th className={s.th}>{c.colScope}</th>
                {canWrite && <th className={s.th}>{c.colActions}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.ruleId}>
                  <td className={s.td}>{r.productName ?? r.productId}</td>
                  <td className={s.td}>{r.crop}</td>
                  <td className={s.td}>{r.market}</td>
                  <td className={s.td}>{r.phiDays}</td>
                  <td className={s.td}>{r.reiHours ?? '—'}</td>
                  <td className={s.td}>{mrlText(r)}</td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={r.scope === 'TENANT' ? 'brand' : 'informative'}>
                      {r.scope === 'TENANT' ? c.scopeTenant : c.scopeGlobal}
                    </Badge>
                  </td>
                  {canWrite && (
                    <td className={s.td}>
                      {r.editable && (
                        <div className={s.actions}>
                          <Button size="small" onClick={() => openEdit(r)}>{c.edit}</Button>
                          <Button size="small" onClick={() => setToDelete(r)}>{c.delete}</Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ComplianceRuleDialog
        open={formOpen}
        rule={editing}
        products={productOptions}
        crops={cropNames}
        saving={saveM.isPending}
        serverError={serverError}
        onCancel={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={(input) => saveM.mutate(input)}
      />

      <ComplianceImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={afterWrite} />

      <Dialog open={!!toDelete} onOpenChange={(_, d) => { if (!d.open) setToDelete(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{c.deleteTitle}</DialogTitle>
            <DialogContent>
              {toDelete && c.deleteConfirm
                .replace('{product}', toDelete.productName ?? toDelete.productId)
                .replace('{crop}', toDelete.crop)
                .replace('{market}', toDelete.market)}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setToDelete(null)}>{c.cancel}</Button>
              <Button appearance="primary" disabled={deleteM.isPending} onClick={() => toDelete && deleteM.mutate(toDelete)}>{c.delete}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
